import React, { useEffect, useState } from 'react';

interface WatermarkProps {
  text?: string;
  fontSize?: number;
  color?: string;
  gap?: number;
  opacity?: number;
}

const Watermark: React.FC<WatermarkProps> = ({
  text = '0xluoye',
  fontSize = 16,
  color = '#000000',
  gap = 100,
  opacity = 0.08,
}) => {
  const [watermarkUrl, setWatermarkUrl] = useState<string>('');

  useEffect(() => {
    // 创建canvas并生成水印
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置画布尺寸
    const angle = -45 * (Math.PI / 180); // 旋转-45度
    canvas.width = gap * 2;
    canvas.height = gap * 2;

    // 绘制水印
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `${fontSize}px Arial`;
    ctx.fillStyle = color;
    ctx.globalAlpha = opacity;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 在画布中心绘制旋转的文字
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);
    ctx.fillText(text, 0, 0);
    ctx.restore();

    // 在其他位置也绘制，使水印更密集
    const offsets = [
      [gap, gap],
      [-gap, -gap],
      [gap, -gap],
      [-gap, gap],
    ];

    offsets.forEach(([offsetX, offsetY]) => {
      ctx.save();
      ctx.translate(canvas.width / 2 + offsetX, canvas.height / 2 + offsetY);
      ctx.rotate(angle);
      ctx.fillText(text, 0, 0);
      ctx.restore();
    });

    // 生成DataURL并保存到state
    const dataUrl = canvas.toDataURL('image/png');
    setWatermarkUrl(dataUrl);
  }, [text, fontSize, color, gap, opacity]);

  if (!watermarkUrl) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        backgroundImage: `url(${watermarkUrl})`,
        backgroundRepeat: 'repeat',
      }}
    />
  );
};

// 创建一个用于图片导出的水印函数
export const addWatermarkToImage = async (
  imageDataURL: string,
  text: string = '0xluoye',
  fontSize: number = 20,
  gap: number = 120,
  opacity: number = 0.15
): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(imageDataURL);
        return;
      }

      // 绘制原始图片
      ctx.drawImage(img, 0, 0);

      // 添加水印
      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = '#000000';
      ctx.globalAlpha = opacity;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const angle = -45 * (Math.PI / 180);

      // 密集绘制水印
      for (let y = -canvas.height; y < canvas.height * 2; y += gap) {
        for (let x = -canvas.width; x < canvas.width * 2; x += gap) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(angle);
          ctx.fillText(text, 0, 0);
          ctx.restore();
        }
      }

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      resolve(imageDataURL);
    };

    img.src = imageDataURL;
  });
};

export default Watermark;
